import { Injectable } from '@angular/core';
import type Board from '@highcharts/dashboards/es-modules/Dashboards/Board';
import type DataTable from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import type { CellType } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import FilterModifier from '@highcharts/dashboards/es-modules/Data/Modifiers/FilterModifier.js';
import type { SerializableCondition } from '@highcharts/dashboards/es-modules/Data/Modifiers/FilterModifierOptions.js';
import type { DashboardFilterHandle } from './dashboard-filter.types';

/**
 * The filter on one Highcharts table, held per column rather than per control: two controls on the
 * same column are two views of one selection, so they cannot disagree and narrow the table to
 * nothing between them.
 */
class TableFilter implements DashboardFilterHandle {
    private readonly selections = new Map<string, readonly CellType[]>();
    private readonly conditions = new Map<string, SerializableCondition>();

    constructor(private readonly table: DataTable) {}

    choices(columnId: string): readonly CellType[] {
        const column = this.table.getColumn(columnId) ?? [];
        return [...new Set(column)].filter((value) => value !== null && value !== undefined);
    }

    selection(columnId: string): readonly CellType[] {
        return this.selections.get(columnId) ?? [];
    }

    async select(columnId: string, values: readonly CellType[]): Promise<void> {
        if (values.length === 0) {
            if (!this.selections.delete(columnId)) {
                return;
            }
        } else {
            this.selections.set(columnId, [...values]);
        }
        await this.table.setModifier(new FilterModifier({ condition: this.compose() }));
    }

    get(key: string): SerializableCondition | undefined {
        return this.conditions.get(key);
    }

    async set(key: string, condition: SerializableCondition | undefined): Promise<void> {
        if (condition !== undefined) {
            this.conditions.set(key, condition);
        } else if (!this.conditions.delete(key)) {
            return;
        }
        await this.table.setModifier(new FilterModifier({ condition: this.compose() }));
    }

    changed(listener: () => void): () => void {
        const unregister = this.table.on('afterSetModifier', () => listener());
        return (): void => {
            unregister();
        };
    }

    /** A table holds one modifier: any of a column's values, and every contributor at once. */
    private compose(): SerializableCondition {
        const columns = [...this.selections.entries()].map(([columnId, values]) => ({
            operator: 'or' as const,
            conditions: values.map((value) => ({
                operator: '===' as const,
                columnId,
                value,
            })),
        }));
        return { operator: 'and', conditions: [...columns, ...this.conditions.values()] };
    }
}

@Injectable({ providedIn: 'root' })
export class DashboardFiltersService {
    /** Weakly keyed, so the filters of a destroyed board are freed along with its tables. */
    private readonly filters = new WeakMap<DataTable, TableFilter>();

    /** The handle for a table, and always the same object for the same table. */
    forTable(table: DataTable): DashboardFilterHandle {
        const existing = this.filters.get(table);
        if (existing) {
            return existing;
        }

        const created = new TableFilter(table);
        this.filters.set(table, created);
        return created;
    }

    /**
     * The handle for a connector's dataset, for code outside the board such as a control outside the board.
     * It is the same dataset the cells read, so setting a condition here filters them too.
     */
    async forConnector(
        board: Board,
        connectorId: string
    ): Promise<DashboardFilterHandle | undefined> {
        try {
            const connector = await board.dataPool.getConnector(connectorId);
            return this.forTable(connector.getTable());
        } catch (cause) {
            console.warn(`pulse-dashboard: no connector '${connectorId}' on this board -`, cause);
            return undefined;
        }
    }
}
